import { Badge, Button, Popover, Typography, styled } from '@mui/material';
import { Stack } from '@mui/system';
import { bindPopover } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect, useRef, useState } from 'react';
import { AICallChannel } from 'src/@types/chat';
import { CustomAvatar } from 'src/components/custom-avatar';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { GuestRequestType } from 'src/provider/VoiceChannel/livekit/types';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { GatheringRequests } from 'src/requests/gathering';
import { useSocketEvent } from 'src/socket';
import {
  onGuestAccepted,
  onGuestJoined,
  onGuestLeft,
} from 'src/socket/listeners/gathering';
import { ActionsBarButtonStyled } from '../../shared/styles';
import { Action } from '../../shared/types';

type Props = {
  isGuest?: boolean;
  isOpened: boolean;
  toggleOpenAction: (action: Action) => void;
};

export const GatheringParticipantsButton = ({
  isGuest,
  isOpened,
  toggleOpenAction,
}: Props) => {
  const { translate } = useLocales();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [lobbyGuest, setLobbyGuest] = useState<GuestRequestType | null>(null);
  const {
    activeVoiceChannel,
    connectionStatus,
    lobbyGuestsRequested,
    addLobbyGuest,
    removeLobbyGuest,
  } = useLivekitRTCState();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'guest-join-popover',
  });

  useSocketEvent(
    onGuestJoined((socket: GuestRequestType) => {
      addLobbyGuest(socket);
    }),
  );

  useSocketEvent(
    onGuestLeft((socket: GuestRequestType) => {
      removeLobbyGuest(socket);
    }),
  );

  useSocketEvent(
    onGuestAccepted((socket: GuestRequestType) => {
      removeLobbyGuest(socket);
    }),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (lobbyGuestsRequested.length && !popupState.isOpen) {
      setLobbyGuest(lobbyGuestsRequested[0]);
      popupState.open();
      return;
    }
    popupState.close();
  }, [lobbyGuestsRequested]);

  const handleAllow = async () => {
    if (!lobbyGuest) return;
    await GatheringRequests.allowGuestUser(lobbyGuest.deviceId);
    removeLobbyGuest(lobbyGuest);
  };

  const handleDeny = async () => {
    if (!lobbyGuest) return;
    await GatheringRequests.denyGuestUser(lobbyGuest.deviceId);
    removeLobbyGuest(lobbyGuest);
  };

  if (
    !activeVoiceChannel?.channel?._id ||
    (activeVoiceChannel.channel as AICallChannel).aiCall ||
    connectionStatus !== 'connected'
  )
    return null;

  return (
    <>
      <Badge
        color="error"
        overlap="circular"
        badgeContent={lobbyGuestsRequested.length}
        sx={{
          '& > .MuiBadge-badge': {
            border: (theme) => `1px solid ${theme.palette.background.default}`,
          },
          cursor: 'pointer',
        }}
        invisible={isGuest}
      >
        <ActionsBarButtonStyled
          ref={buttonRef}
          variant="outlined"
          color={isOpened ? 'primary' : 'inherit'}
          onClick={() => toggleOpenAction('gathering')}
          tooltip={translate('actions_bar.gathering_participants')}
          placement="left"
        >
          <Iconify icon="tabler:users" />
        </ActionsBarButtonStyled>
      </Badge>
      {popupState.isOpen && !isGuest && (
        <StyledPopover
          {...bindPopover(popupState)}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          anchorEl={buttonRef.current}
        >
          <InviteStyled>
            <div className="inviteContainer">
              <Typography variant="body2" sx={{ textWrap: 'nowrap' }}>
                Somebody wants to join this call:
              </Typography>
              <Stack direction="row" alignItems="center" gap={2}>
                <Stack direction="row" alignItems="center" gap={1} mr={1}>
                  <CustomAvatar
                    alt={lobbyGuest?.name}
                    name={lobbyGuest?.name}
                    src={lobbyGuest?.picture}
                    sx={{ height: 28, width: 28 }}
                  />
                  <Typography variant="body2">{lobbyGuest?.name}</Typography>
                </Stack>
                <Button variant="outlined" color="inherit" onClick={handleDeny}>
                  Deny
                </Button>
                <Button variant="contained" onClick={handleAllow}>
                  Allow
                </Button>
              </Stack>
            </div>
          </InviteStyled>
        </StyledPopover>
      )}
    </>
  );
};

const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: 'transparent',
    border: 'none',
    marginTop: theme.spacing(-2),
    boxShadow: 'none',
    paddingRight: theme.spacing(0.5),
  },
}));

const InviteStyled = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  '.inviteContainer': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.customShadows.dialog,
  },
  '&:after': {
    content: '""',
    display: 'block',
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    marginLeft: theme.spacing(-1),
    transform: 'rotate(45deg)',
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius / 2,
    border: `1px solid ${theme.palette.divider}`,
    borderColor: `${theme.palette.divider} ${theme.palette.divider} transparent transparent`,
  },
}));
